import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from "@/components/ui-kit";
import { evTitle } from "@/components/EventCard";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { AppEvent } from "@/types";

export const ExternalBookingDialog = ({ e, onClose, onDone }: { e: AppEvent; onClose: () => void; onDone?: () => void }) => {
  const { t, lang } = usePrefs();
  const [f, setF] = useState({ name: "", phone: "", guests: 2, note: "", seating: "" });
  const [busy, setBusy] = useState(false);
  const field = (k: "name" | "phone" | "note", label: string, props: any = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`x-${k}`}>{t(label)}</Label>
      <Input id={`x-${k}`} value={f[k]} onChange={(ev: any) => setF({ ...f, [k]: ev.target.value })} data-testid={`external-${k}-input`} className="h-11 rounded-2xl" {...props} />
    </div>
  );
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/venue/events/${e.event_id}/external`, { ...f, seating: f.seating || undefined });
      toast.success(t("b5.added"));
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="external-booking-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-display">{t("b5.externalTitle")}</DialogTitle></DialogHeader>
        <p className="text-sm font-bold">{evTitle(e, lang)}</p>
        <p className="text-xs text-muted-foreground">{t("b5.externalNote")}</p>
        {field("name", "b5.name")}
        {field("phone", "b5.phone", { dir: "ltr", inputMode: "tel" })}
        <div className="space-y-1.5">
          <Label htmlFor="x-guests">{t("bk.people")}</Label>
          <Input id="x-guests" type="number" min={1} max={500} value={f.guests} onChange={(ev: any) => setF({ ...f, guests: Number(ev.target.value) })} data-testid="external-guests-input" className="h-11 rounded-2xl" />
        </div>
        {!!e.seating_types?.length && (
          <div className="space-y-1.5"><Label>{t("seat.type")}</Label>
            <select value={f.seating} onChange={(ev) => setF({ ...f, seating: ev.target.value })} data-testid="external-seating-select" className="h-11 w-full rounded-2xl border bg-background px-3 text-sm">
              <option value="">—</option>{e.seating_types.map((s) => <option key={s} value={s}>{t(`seat.${s}`)}</option>)}
            </select></div>
        )}
        {field("note", "b5.note")}
        <button onClick={submit} disabled={busy || f.name.trim().length < 2 || f.guests < 1} data-testid="external-submit" className="h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50">
          {busy ? t("common.loading") : t("b5.send")}
        </button>
      </DialogContent>
    </Dialog>
  );
};
